import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, X, Send, Loader2, Bot, User, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem("cm_assistant_session");
  if (!sid) {
    sid = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("cm_assistant_session", sid);
  }
  return sid;
}

const HIDDEN_PREFIXES = ["/admin", "/presentation", "/styles", "/preview-access"];

export default function ChatWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm the ClassMate assistant. Ask me anything — I'll point you to the right course, exam or video. You can also write in your own language.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  const courseMatch = matchPath("/course/:courseId", location.pathname);
  const courseId = courseMatch?.params?.courseId || null;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = { role: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMessage, { role: "assistant", content: "", links: [] }]);
    setInput("");
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: userMessage.content,
          course_id: courseId,
          page_path: location.pathname,
          session_id: getSessionId(),
        }),
      });
      if (response.status === 402) {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: "assistant",
            content: "You've reached today's free AI limit (5 messages). Upgrade to ClassMate Premium for unlimited AI help.",
            links: [{ title: "Upgrade to Premium", path: "/pricing" }],
          };
          return next;
        });
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let ev;
          try { ev = JSON.parse(part.slice(6)); } catch { continue; }
          if (ev.type === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + ev.text };
              return next;
            });
          } else if (ev.type === "done") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], links: ev.links || [] };
              return next;
            });
          } else if (ev.type === "error") {
            errored = true;
          }
        }
      }
      if (errored) {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!last.content) next[next.length - 1] = { ...last, content: "Something went wrong — please try again." };
          return next;
        });
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "I'm having trouble connecting. Please try again.", links: [] };
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 h-14 w-14 rounded-full shadow-lg float-shadow bounce-subtle z-50"
        data-testid="chat-toggle-btn"
      >
        <MessageSquare className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className="chat-panel glass" data-testid="chat-panel">
      <div className="chat-panel-header glass">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">ClassMate Assistant</h3>
            <p className="text-xs text-muted-foreground">Find courses, exams & videos</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="h-8 w-8 rounded-full" data-testid="chat-close-btn">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 h-[400px] p-4" ref={scrollRef} data-testid="chat-messages">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-2 ${message.role === "user" ? "justify-end" : "justify-start"}`}>
              {message.role === "assistant" && (
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              <div className={`max-w-[80%] ${message.role === "user" ? "chat-bubble-user" : "chat-bubble-assistant"}`}>
                <p className="text-sm whitespace-pre-wrap">
                  {message.content || (isLoading && index === messages.length - 1 ? "…" : "")}
                </p>
                {message.links?.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1.5" data-testid="chat-nav-links">
                    {message.links.map((l, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        className="justify-between h-8 text-xs"
                        data-testid={`chat-nav-link-${i}`}
                        onClick={() => navigate(l.path)}
                      >
                        <span className="truncate">{l.title}</span>
                        <ArrowRight className="h-3 w-3 flex-shrink-0" />
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              {message.role === "user" && (
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="p-3 border-t flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything…"
          disabled={isLoading}
          data-testid="chat-input"
        />
        <Button onClick={sendMessage} disabled={isLoading || !input.trim()} size="icon" data-testid="chat-send-btn">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </Card>
  );
}
